/**
 * Thin clients for Group Therapy Medplum bots (appointment generate, waitlist fill, access sync).
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

const BOT_SYSTEM = 'https://unify.health/bots';

function botTarget(value: string, envId?: string): string | Identifier {
  const id = envId?.trim();
  if (id) return id;
  return { system: BOT_SYSTEM, value };
}

async function executeGroupBot<T extends object>(
  medplum: MedplumClient,
  target: string | Identifier,
  input: unknown,
  emptyMessage: string,
): Promise<T> {
  const result = await medplum.executeBot(target, input, ContentType.JSON);
  if (!result || typeof result !== 'object') throw new Error(emptyMessage);
  return result as T;
}

export type AppointmentSeriesGenerateAction = 'generate' | 'reconcile' | 'sync-participants';

export type SoftScheduleConflict = {
  appointmentStart: string;
  actorType: 'room' | 'device';
  actorId: string;
  actorDisplay: string;
  conflictingSlotStart?: string;
  conflictingSlotEnd?: string;
};

export type AppointmentSeriesGenerateResult = {
  action: AppointmentSeriesGenerateAction;
  groupId: string;
  created: string[];
  skipped: string[];
  cancelled: string[];
  updated: string[];
  conflicts: SoftScheduleConflict[];
  conflictMessages?: string[];
};

export async function requestAppointmentSeriesGenerate(
  medplum: MedplumClient,
  groupId: string,
  action: AppointmentSeriesGenerateAction = 'generate',
): Promise<AppointmentSeriesGenerateResult> {
  return executeGroupBot(
    medplum,
    botTarget(
      'appointment-series-generate',
      import.meta.env.VITE_MEDPLUM_APPOINTMENT_SERIES_GENERATE_BOT_ID
        ?? import.meta.env.VITE_MEDPLUM_GROUP_APPOINTMENT_GENERATE_BOT_ID,
    ),
    { action, groupId },
    'Appointment series generate bot returned an empty result',
  );
}

export async function requestAppointmentSeriesSyncParticipants(
  medplum: MedplumClient,
  groupId: string,
): Promise<AppointmentSeriesGenerateResult> {
  return requestAppointmentSeriesGenerate(medplum, groupId, 'sync-participants');
}

/** @deprecated Use AppointmentSeriesGenerateAction. */
export type GroupAppointmentGenerateAction = AppointmentSeriesGenerateAction;
/** @deprecated Use AppointmentSeriesGenerateResult. */
export type GroupAppointmentGenerateResult = AppointmentSeriesGenerateResult;
/** @deprecated Use requestAppointmentSeriesGenerate. */
export const requestGroupAppointmentGenerate = requestAppointmentSeriesGenerate;
/** @deprecated Use requestAppointmentSeriesSyncParticipants. */
export const requestGroupAppointmentSyncParticipants = requestAppointmentSeriesSyncParticipants;

export type GroupAccessSyncAction = 'enroll' | 'unenroll' | 'facilitators' | 'discharge';
